import { RowObj } from "components/FieldTypes/PhotoLog/PhotoLog";
import { FieldInstance, FieldInstanceForResolver, FieldTemplate, Image, RequirePropAsType } from "db/definitions";
import { recordSentry } from "utils/recordSentry";
import { v4 as uuid } from "uuid";
import { bulkSaveFieldInstances, createFieldForBatchResolver, updateFieldInstance } from "../db/queries/fieldInstanceQueries";
import { createImageBinary, getImageBinaryById } from "../db/queries/imageBinaryQueries";
import { bulkSaveImageDocs, createImageForBatchResolver } from "../db/queries/imageQueries";
import updateSingleFieldInstance from "./_updateSingleFieldInstance";
import batchCreateFieldInstances from "./batchCreateFieldInstances";
import batchCreateImages from "./batchCreateImages";
import { handleBatchCrud } from "./handleBatchCrud";
import { handleToolboxResponse } from "./s3/handleToolboxResponse";
import copyImageRoute from "./toolboxQueries/copyImageRoute";
import { picklistTypes } from "assets/json/global";

/**
 * Creates copies of the provided rowObjs to the provided imageroll
 * @param {FieldInstance} parentFieldInstance to add the image rows into.
 * @param {Array} rowObjects rowObj containing rowKey, image and fields data that needs to be copied.
 * @param {boolean} online Whether or not the user is online
 */
async function batchUploadFromPhotolog(parentFieldInstance: FieldInstance, rowObjects: RowObj[], online: boolean): Promise<RowObj[]> {
	const imageDocs = [];
	const fieldInstances = [];
	const newFieldsOrderItems = parentFieldInstance.fieldsOrder || [];
	const templateOrderMap: Record<string, number> = {};
	const templateRollLinkIdMap: Record<string, FieldTemplate> = {};
	const templateIdMap: Record<string, FieldTemplate> = {};

	// setup a map for the template position in the row id: index
	if (parentFieldInstance.fieldTemplate?.rowOrder) {
		for (let i = 0; i < parentFieldInstance.fieldTemplate.rowOrder.length; i++) {
			const templateId = parentFieldInstance.fieldTemplate.rowOrder[i];
			templateOrderMap[templateId] = i;
		}
	}

	// need to setup a map with the rollLinkedId as the key and the template id as the value
	if (parentFieldInstance.fieldTemplate?.tableFields) {
		for (let i = 0; i < parentFieldInstance.fieldTemplate.tableFields.items.length; i++) {
			const template = parentFieldInstance.fieldTemplate.tableFields.items[i];
			// const template = tableField.fieldTemplate;
			if (template.rollLinkId) {
				templateRollLinkIdMap[template.rollLinkId] = template;
			}
			templateIdMap[template.id] = template;
		}
	}
	//  make the copies, must set them up to use the parent field's templates rather than the
	// row Obj which is the one coming from the photolog itself
	const newRowObjs: RowObj[] = [];
	for (let i = 0; i < rowObjects.length; i++) {
		const rowObj = rowObjects[i];
		let rowStringArray = [];
		// row fields
		// this alreadyAdded is a map to track the templates as we add them in, so we can create blank instances
		// for templates that arent in the photolog, but are in the instance.
		let alreadyAdded: Record<string, string> = {};
		const newFieldsArray: FieldInstance[] = [];
		let newRowKey = ``;
		for (let j = 0; j < rowObj.fields.length; j++) {
			const field = rowObj.fields[j];
			if (!field.fieldTemplate) {
				recordSentry({ error: "Field template missing on field in photolog batch upload", msg: JSON.stringify(field) });
				continue;
			}
			const rollLinkId = field.fieldTemplate?.rollLinkId;
			let templateId;
			if (rollLinkId && templateRollLinkIdMap[rollLinkId]) {
				// it has a roll link id and it exists in this map.
				templateId = templateRollLinkIdMap[rollLinkId].id;
			} else {
				// if it doesnt have a rollLinkId or isnt matched then the field is not on the template
				// that is attempting to be pulled in, so it just gets skipped!
				continue;
			}

			const fieldCopy = createFieldForBatchResolver(field);
			fieldCopy.id = uuid();
			fieldCopy.tableField = parentFieldInstance.id;
			fieldCopy.formGroup = parentFieldInstance.formGroup;
			fieldCopy.fieldTemplateId = templateId;

			// This portion is to make sure if the selected option from Photolog has a linkedId, then it needs to match
			// with the new template's option that has the same linkedId.
			const isPicklist = picklistTypes.includes(field.fieldTemplate?.inputType);
			if (isPicklist && field.fieldTemplate?.options) {
				if (field.value !== "") {
					const linkedIdOption = field.fieldTemplate?.options?.items.find((opt) => opt.id === field.value);
					if (linkedIdOption && linkedIdOption.linkedId) {
						const newOption = templateIdMap[templateId].options?.items.find((opt) => opt.linkedId === linkedIdOption?.linkedId);
						// Need to figure out fields with options and linkedIds on those options if the value is an id or the fieldTemplate is a checkbox or dropdown or radio type
						if (newOption) {
							fieldCopy.value = newOption.id;
						}
					} else if (linkedIdOption && !linkedIdOption.linkedId) {
						// If there is no linkedId, we can throw an error and set the value to the selected options value prop
						// This will be an issue for instances where the value needs to be transcribed differently than the label in the photolog's version
						// so for now im just commenting this out so we dont think its working when its not.
						// fieldCopy.value = linkedIdOption?.value || "";
						recordSentry({ error: "Option selected in photolog picklist does not have a linkedId", msg: JSON.stringify(field) });
					}
				}
			}

			fieldInstances.push(fieldCopy);
			const newFieldsInstance: FieldInstance = { ...fieldCopy, fieldTemplate: { ...templateRollLinkIdMap[rollLinkId] }, tableFields: { items: [] } };
			newFieldsArray.push(newFieldsInstance);
			newRowKey += newRowKey ? `,${fieldCopy.id}` : fieldCopy.id;
			alreadyAdded[templateId] = templateId;
			const indexToAddItTo = templateOrderMap[templateId];
			if (typeof indexToAddItTo !== "number") {
				recordSentry({ error: "Error setting up fields order, template not added to map for ordering field", msg: JSON.stringify(field) });
			} else {
				rowStringArray[indexToAddItTo] = fieldCopy.id;
			}
		}

		// Images need to be setup with rollLinkId === "IMAGE"
		const imageTemplate = templateRollLinkIdMap["IMAGE"];

		// image field - needs to upload the image successfully
		// can make copy of the image, up to 5gb
		const imageResult = await makeCopyOfImageField(rowObj.image, parentFieldInstance, online, imageTemplate.id);
		const imageForRowObj: RequirePropAsType<FieldInstance, "image", Image> = {
			...imageResult.field,
			image: { ...imageResult.image },
			fieldTemplate: { ...imageTemplate },
			tableFields: { items: [] },
		};
		if (imageResult.field.fieldTemplateId) {
			alreadyAdded[imageResult.field.fieldTemplateId] = imageResult.field.fieldTemplateId;
			fieldInstances.push(imageResult.field);
			imageDocs.push(imageResult.image);
		}

		//Going to need to go through the rowOrder and make sure that every template makes it in the
		// final piece.  Each one will just be created as a blank field instance.
		// This has to happen here within the rowObject
		if (parentFieldInstance.fieldTemplate?.rowOrder && Object.keys(alreadyAdded).length !== parentFieldInstance.fieldTemplate.rowOrder.length) {
			// There are either too many or not enough!!
			if (Object.keys(alreadyAdded).length < parentFieldInstance.fieldTemplate.rowOrder.length) {
				for (let i = 0; i < parentFieldInstance.fieldTemplate.rowOrder.length; i++) {
					const idToCheck: string = parentFieldInstance.fieldTemplate.rowOrder[i];
					if (!alreadyAdded[idToCheck]) {
						// This field needs to be created.
						const templateToCreateFrom = templateIdMap[idToCheck];
						const blankField = createBlankFieldInstance(templateToCreateFrom, parentFieldInstance);
						fieldInstances.push(blankField);

						const indexToAddItTo = templateOrderMap[idToCheck];
						if (typeof indexToAddItTo !== "number") {
							recordSentry({
								error: "Error setting up row string array, template not added to map for ordering field",
								msg: JSON.stringify(templateToCreateFrom),
							});
						} else {
							rowStringArray[indexToAddItTo] = blankField.id;
						}
					}
				}
			} else {
				// the already added actually has more than the other template for some reason...
				// IF YOU ARE HITTING THIS LINE THEN YOU COULD BE THE FIRST ONE TO DO SO!
				// PRETTY SURE WE CAN JUST SKIP THESE BUT THERE COULD BE AN ISSUE WHEN WE ACTUALLY HAVE ONE
				// THIS IS PROBBALY GOING TO BE A SEPARATE TICKET TO TACKLE SO REACH OUT TO TEAM LEAD
				// IF THIS HAPPENS!
				console.log("Somehow getting extra fields.... ", alreadyAdded, parentFieldInstance);
			}
		}
		// add the image on the beginning dis is important and there actually shouldnt be anything at
		// element 0 anyways!!!
		rowStringArray[0] = imageResult.field.id;
		const rowString = rowStringArray.join(",");
		newFieldsOrderItems.push(rowString);
		alreadyAdded = {};
		newRowObjs.push({ image: imageForRowObj, fields: newFieldsArray, rowKey: newRowKey });
	}

	// Need to update fieldsOrder!!!
	const forceUpdateData = `${Date.now()}-tableFields`;
	const {
		authors,
		fieldTemplateId,
		formGroup,
		formInstance,
		group,
		id,
		// updatedAt?
	} = parentFieldInstance;

	const updatedImageRoll = {
		fieldsOrder: newFieldsOrderItems,
		authors,
		fieldTemplateId,
		formGroup,
		formInstance,
		group,
		id,
		forceUpdateData,
	};
	// batch upload all fields and all images.
	if (online) {
		await handleBatchCrud(fieldInstances, batchCreateFieldInstances, 25);
		await handleBatchCrud(imageDocs, batchCreateImages, 25);
		// trigger forceUpdateData on the parentField
		await updateSingleFieldInstance(updatedImageRoll);
	} else {
		await Promise.all([bulkSaveFieldInstances(fieldInstances), bulkSaveImageDocs(imageDocs), updateFieldInstance(updatedImageRoll)]);
	}
	// const fieldTemplate = parentFieldInstance.fieldTemplate;
	// const updatedImageRollFinal = { ...updatedImageRoll, fieldTemplate };
	return newRowObjs;
}

/**
 * Creates blank instance
 * @param template that the new instance must use.
 */
function createBlankFieldInstance(template: FieldTemplate, parentField: FieldInstanceForResolver): FieldInstanceForResolver {
	const newId = uuid();
	// setup the new object, with some properties changed
	const timestamp = new Date().toISOString();
	const newFieldInstance: FieldInstanceForResolver = {
		id: newId,
		formGroup: parentField.formGroup,
		tableField: parentField.id,
		value: "",
		fieldTemplateId: template.id,
		group: parentField.group,
		authors: parentField.authors,
		lastAuthor: "",
		fieldsOrder: [],
		notes: "",
		editing: "",
		createdAt: timestamp,
		updatedAt: timestamp,
	};

	return newFieldInstance;
}

/**
 * Copies a field instance for an image field.
 * @param fieldToCopy the document.
 * @param parentField mommiiee
 * @param online Whether or not the user is online
 * @param templateId The id of the image fieldTemplate
 * @returns The copied field instance and image document
 */
async function makeCopyOfImageField(
	fieldToCopy: RequirePropAsType<FieldInstance, "image", Image>,
	parentField: FieldInstance,
	online: boolean,
	templateId: string,
): Promise<{ image: Image; field: FieldInstanceForResolver }> {
	const newId = uuid();
	const { value, group, authors, image } = fieldToCopy;
	// setup the new object, with some properties changed

	const imageCopyResult = await handleImageObjCopy(image, online);

	const newImageDoc = createImageForBatchResolver(image);
	const newImageInstance: Pick<FieldInstance, "formGroup" | "group"> & Partial<FieldInstance> = {
		id: newId,
		formGroup: parentField.formGroup,
		tableField: parentField.id,
		value,
		fieldTemplateId: templateId,
		group,
		authors,
	};
	const newFieldInstance = createFieldForBatchResolver(newImageInstance);
	newImageDoc.id = uuid();
	newImageDoc.key = imageCopyResult;
	newImageDoc.formGroup = parentField.formGroup;
	newFieldInstance.imageId = newImageDoc.id;

	const result = {
		image: newImageDoc,
		field: newFieldInstance,
	};

	return result;
}

/**
 * Copies the image doc and image and returns the new image doc
 * @author Eric Webb <ewebb@factorearth.com>
 * @param imageDoc The image document that needs its image copied
 * @param online Whether or not the user is online
 * @returns The key of the copied image.
 */
export async function handleImageObjCopy(imageDoc: Image, online: boolean): Promise<string> {
	try {
		if (imageDoc.key === "" || typeof imageDoc.key === "object") {
			return "";
		}
		const stringArray = imageDoc.key.split("/");
		let fileNameArray = stringArray[1].split("-");
		fileNameArray.splice(0, 1);
		let fileName = fileNameArray.join("");
		const finalFileName = Date.now() + "-" + fileName;
		const groupName = stringArray[0];
		const newPutKey = `${groupName}/${finalFileName}`;
		if (online) {
			const newKey = await handleS3ObjectCopy(imageDoc.key);
			return newKey;
		} else {
			const imageBinary = await getImageBinaryById(imageDoc.key);
			if (imageBinary) {
				imageBinary.key = newPutKey;

				// Create a new File object with the correct name
				const newFile = new File([imageBinary.binary], newPutKey, {
					type: imageBinary.binary.type,
					lastModified: imageBinary.binary.lastModified,
				});

				imageBinary.binary = newFile;
				await createImageBinary(imageBinary);
				return newPutKey;
			}
		}
	} catch (error) {
		recordSentry({ error, msg: "Error copying image:" });
	}
	return "";
}

/**
 * Given an object key, copies it and returns the new key for it
 * @author Eric Webb <ewebb@factorearth.com>
 * @param originalKey The key for the image we're copying
 * @returns The key for the copied image
 */
async function handleS3ObjectCopy(originalKey: string): Promise<string> {
	try {
		const response = await copyImageRoute(originalKey);
		const newKey = handleToolboxResponse(response);
		return newKey;
	} catch (err) {
		recordSentry({ error: err, msg: "Error copying image:" });
	}
	return "";
}

export default batchUploadFromPhotolog;
